import React, { useMemo } from 'react';

import isValid from 'date-fns/isValid';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import RiskIcon from '@atlaskit/icon-lab/core/risk';
import IconTile from '@atlaskit/icon/icon-tile';
import type { SemanticColor } from '@atlaskit/lozenge/types';
import { Box } from '@atlaskit/primitives/compiled';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { StatusTargetDateDisplay } from '../../../../../../common/ui/status-target-date-display';
import { TargetDate } from '../../../../../../common/ui/target-date-display';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { getRiskIconAppearance } from '../../../../../../common/utils/mercury-risk';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchMercuryRisk_AtlassianSearchPage$key } from './__generated__/searchMercuryRisk_AtlassianSearchPage.graphql';

const messages = defineMessages({
	draft: {
		id: 'search-page.result.mercury.risk.status.draft',
		defaultMessage: 'Draft',
		description: 'Label for Mercury Risk status draft',
	},
	canceled: {
		id: 'search-page.result.mercury.risk.status.canceled',
		defaultMessage: 'Canceled',
		description: 'Label for Mercury Risk status canceled',
	},
	accepted: {
		id: 'search-page.result.mercury.risk.status.accepted',
		defaultMessage: 'Accepted',
		description: 'Label for Mercury Risk status accepted',
	},
	resolved: {
		id: 'search-page.result.mercury.risk.status.resolved',
		defaultMessage: 'Resolved',
		description: 'Label for Mercury Risk status resolved',
	},
	iconLabel: {
		id: 'search-page.result.mercury.risk.icon.label',
		defaultMessage: 'Risk',
		description: 'Accessible label for the Mercury Risk search result icon',
	},
});

const getRiskStatusLabel = (
	statusKey: string | undefined,
	statusDisplayName: string | undefined,
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): string | undefined => {
	switch (statusKey?.toLowerCase()) {
		case 'draft':
			return formatMessage(messages.draft);
		case 'canceled':
			return formatMessage(messages.canceled);
		case 'accepted':
			return formatMessage(messages.accepted);
		case 'resolved':
			return formatMessage(messages.resolved);
		default:
			// Fall back to the backend-provided display name for any status key not
			// explicitly translated above, rather than constructing a non-i18n string.
			return statusDisplayName;
	}
};

const getRiskStatusAppearance = (statusKey?: string): SemanticColor => {
	switch (statusKey?.toLowerCase()) {
		case 'draft':
			return 'neutral';
		case 'canceled':
			return 'neutral';
		case 'accepted':
			return 'information';
		case 'resolved':
			return 'success';
		default:
			return 'neutral';
	}
};

export const SearchMercuryRisk = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchMercuryRisk_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const data = useFragment(
		graphql`
			fragment searchMercuryRisk_AtlassianSearchPage on SearchResult {
				__typename
				id
				title
				type
				url
				... on SearchResultMercuryRisk {
					lastModifiedDate
					risk @optIn(to: "Mercury") {
						status {
							key
							displayName
						}
						targetDate {
							targetDate
							targetDateType
						}
					}
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type]);

	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const title = useMemo(() => enrichExcerpt(data.title), [data.title]);
	const headerMetadataParts = useMemo(
		() => [
			[
				'type',
				<Box>
					<SearchResultType type={data.type as SearchClientResultType} />
				</Box>,
			],
			[
				'last-modified',
				data.lastModifiedDate ? (
					<Box>
						<LastModified lastModified={data.lastModifiedDate} />
					</Box>
				) : null,
			],
		],
		[data.type, data.lastModifiedDate],
	);

	const description = useMemo(() => {
		const displayedStatus = getRiskStatusLabel(
			data.risk?.status?.key,
			data.risk?.status?.displayName,
			formatMessage,
		);

		if (!displayedStatus) {
			return null;
		}

		const targetDate = data.risk?.targetDate?.targetDate;
		const targetDateType = data.risk?.targetDate?.targetDateType;
		const displayedTargetDate =
			(targetDate && targetDateType && isValid(new Date(targetDate)) && (
				<TargetDate targetDate={targetDate} targetDateType={targetDateType} />
			)) ||
			undefined;

		return (
			<StatusTargetDateDisplay
				displayedStatus={displayedStatus}
				displayedStatusAppearance={getRiskStatusAppearance(data.risk?.status?.key)}
				displayedTargetDate={displayedTargetDate}
			/>
		);
	}, [
		data.risk?.status?.displayName,
		data.risk?.status?.key,
		data.risk?.targetDate?.targetDate,
		data.risk?.targetDate?.targetDateType,
		formatMessage,
	]);

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Focus}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={data.url}
			title={title}
			icon={
				<IconTile
					icon={RiskIcon}
					label={formatMessage(messages.iconLabel)}
					appearance={getRiskIconAppearance(data.risk?.status?.key)}
					size="medium"
				/>
			}
			headerMetadataParts={headerMetadataParts}
			description={description}
			cloudId={cloudId}
			position={position}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchMercuryRisk = (
	node?: any,
): node is searchMercuryRisk_AtlassianSearchPage$key => {
	return node?.id?.includes('mercury') && node.__typename === 'SearchResultMercuryRisk';
};
